import { DatePicker } from '@mui/x-date-pickers';
import { validateProps } from '../../../../../utils/propValidator';
import { FormFieldWrapper } from '../../layout';

const propRules = {
  id: { type: 'string' },
  label: { type: 'string' },

  value: { type: ['any', 'null'] }, // dayjs | null
  onChange: { type: 'function', required: true },

  disabled: { type: 'boolean' },
  minDate: { type: 'any' },
  maxDate: { type: 'any' },
  disablePast: { type: 'boolean' },
  disableFuture: { type: 'boolean' },
  views: { type: 'array' },
  format: { type: 'string' },

  error: { type: 'boolean' },
  errorMessage: { type: 'string' },
  helperText: { type: 'string' },
  showErrorInline: { type: 'boolean' },
  reserveErrorSpace: { type: 'boolean' },
};

/**
 * BaseDatePicker
 *
 * Pure controlled date picker component.
 * No form-library awareness.
 *
 * VALUE CONTRACT
 * - value: dayjs | null
 * - onChange: (dayjs | null) => void
 *
 * @param {Object} props
 *
 * @param {string} [props.id] - HTML id applied to the input.
 * @param {string} [props.label] - Field label rendered by FormFieldWrapper.
 * @param {import('dayjs').Dayjs | null} props.value - Controlled date value. Must be a dayjs instance or null.
 * @param {(nextValue: import('dayjs').Dayjs | null) => void} props.onChange - Called when the date changes. Emits dayjs or null never strings or events.
 * @param {boolean} [props.disabled=false] - Disables the date picker.
 * @param {import('dayjs').Dayjs} [props.minDate] - Minimum selectable date.
 * @param {import('dayjs').Dayjs} [props.maxDate] - Maximum selectable date.
 * @param {boolean} [props.disablePast=false] - Prevents selection of past dates.
 * @param {boolean} [props.disableFuture=false] - Prevents selection of future dates.
 * @param {Array<'day'|'month'|'year'>} [props.views=['day','month','year']] - Views enabled in the picker.
 * @param {string} [props.format='DD/MM/YYYY'] - Display format for the input.
 * @param {boolean} [props.error=false] - Whether the field is in an error state.
 * @param {string} [props.errorMessage] - Error message rendered inline when `error` is true.
 * @param {string} [props.helperText] - Non-error helper text rendered alongside errors.
 * @param {boolean} [props.showErrorInline=true] - Controls whether the error is rendered by this component - or handled by a parent (e.g. ErrorSummary).
 * @param {boolean} [props.reserveErrorSpace=false] - Reserves vertical space for error/helper text - to prevent layout shift.
 *
 * @returns {React.ReactElement}
 */
export default function BaseDatePicker(props: any) {
  validateProps(props as Record<string, unknown>, propRules, 'BaseDatePicker');

  const {
    id,
    label,
    value,
    onChange,
    disabled,
    minDate,
    maxDate,
    disablePast,
    disableFuture,
    views = ['day', 'month', 'year'],
    format = 'DD/MM/YYYY',

    error = false,
    errorMessage,
    helperText,
    showErrorInline = true,
    reserveErrorSpace = false,
    wrapperSx,
    orientation,

    daySx = {},
    monthButtonSx = {},
    yearButtonSx = {},
    slotProps = {},

    ...rest
  } = props;

  const viewStyle = {
    fontSize: '0.875rem',
    borderRadius: '8px',
    '&.Mui-selected': {
      backgroundColor: (theme) => `${theme.palette.primary.light} !important`,
    },
  };

  const shouldShowError = Boolean(showErrorInline && error);

  return (
    <FormFieldWrapper
      label={label}
      htmlFor={id}
      error={showErrorInline ? error : false}
      errorMessage={showErrorInline ? errorMessage : undefined}
      helperText={helperText}
      reserveErrorSpace={reserveErrorSpace}
      sx={wrapperSx}
      orientation={orientation}
    >
      <DatePicker
        {...rest}
        value={value}
        onChange={(next) => onChange(next ?? null)}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        disablePast={disablePast}
        disableFuture={disableFuture}
        views={views}
        format={format}
        slotProps={{
          textField: {
            id,
            size: 'small',
            variant: 'outlined',
            fullWidth: true,
            error: shouldShowError,
            helperText: null, // IMPORTANT: wrapper owns error text
          },
          inputAdornment: { sx: { mr: 1.5 } },
          popper: { sx: { '& .MuiDateCalendar-root': { height: 'auto' } } },
          openPickerIcon: { sx: { width: 18, height: 18 } },
          day: { sx: { ...viewStyle, ...daySx } },
          monthButton: { sx: { ...viewStyle, ...monthButtonSx } },
          yearButton: { sx: { ...viewStyle, ...yearButtonSx } },
          ...slotProps,
        }}
      />
    </FormFieldWrapper>
  );
}
